import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInventory } from '../../hooks/useInventory';
import { inventoryService } from '../../services/inventoryService';
import Button from '../../components/ui/Button';
import SearchInput from '../../components/ui/SearchInput';
import ResponsiveTable from '../../components/ui/ResponsiveTable';
import Card from '../../components/ui/Card';
import Breadcrumb from '../../components/ui/Breadcrumb';

const InventoryList: React.FC = () => {
  const navigate = useNavigate();
  const { items, loading, error, refreshInventory } = useInventory();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');

  const filteredItems = items.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterCategory === 'all' || item.category === filterCategory;
    
    return matchesSearch && matchesFilter;
  });

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this inventory item?')) {
      try {
        const { error } = await inventoryService.deleteInventoryItem(id);
        if (error) {
          alert(error.message);
        } else {
          refreshInventory();
        }
      } catch (err) {
        alert('Failed to delete inventory item');
      }
    }
  };

  const getCategoryLabel = (category: string): string => {
    return inventoryService.getCategoryLabel(category);
  };

  const getFormattedCost = (cost: number): string => {
    return inventoryService.getFormattedCost(cost);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative">
        {error}
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Inventory', current: true }
  ];

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
        <Button variant="primary" onClick={() => navigate('/inventory/new')}>
          Add New Item
        </Button>
      </div>

      <div className="mb-6 space-y-4">
        <div className="max-w-md">
          <SearchInput
            placeholder="Search inventory..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter - Tabs on desktop, dropdown on mobile */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {[
            { value: 'all', label: 'All Categories' },
            { value: 'spare_parts', label: 'Spare Parts' },
            { value: 'accessories', label: 'Accessories' },
            { value: 'devices', label: 'Devices' }
          ].map((tab) => (
            <button
              key={tab.value}
              onClick={() => setFilterCategory(tab.value)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                filterCategory === tab.value
                  ? 'bg-primary-100 text-primary-700 border border-primary-300'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Mobile dropdown */}
        <div className="sm:hidden">
          <select
            className="input-field w-full"
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
          >
            <option value="all">All Categories</option>
            <option value="spare_parts">Spare Parts</option>
            <option value="accessories">Accessories</option>
            <option value="devices">Devices</option>
          </select>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <ResponsiveTable
          headers={['Name', 'Category', 'Quantity', 'Average Cost', 'Total Value']}
          rows={filteredItems.map(item => [
            item.name,
            <span className="badge badge-info">
              {getCategoryLabel(item.category)}
            </span>,
            <span className={item.quantity <= item.reorder_point ? 'text-red-600 font-medium' : ''}>
              {item.quantity}
              {item.quantity <= item.reorder_point && (
                <span className="ml-2 text-xs text-red-600">(Low Stock)</span>
              )}
            </span>,
            getFormattedCost(item.average_cost),
            getFormattedCost(item.sale_price * item.quantity)
          ])}
          renderRow={(row, index) => {
            const item = filteredItems[index];
            return (
              <tr
                key={item.id}
                onClick={() => navigate(`/inventory/${item.id}`)}
                className="cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {row[0]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[1]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[2]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[3]}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {row[4]}
                </td>
              </tr>
            );
          }}
          renderMobileCard={(row, index) => {
            const item = filteredItems[index];
            return (
              <Card
                key={item.id}
                onClick={() => navigate(`/inventory/${item.id}`)}
                className="mb-6 cursor-pointer hover:bg-gray-50 transition-colors duration-200"
              >
                <div className="px-4 py-5 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900">
                        {row[0]}
                      </h3>
                      <div className="mt-2 grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Category</p>
                          <p className="text-sm font-medium text-gray-900">{row[1]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Quantity</p>
                          <p className="text-sm font-medium text-gray-900">{row[2]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Average Cost</p>
                          <p className="text-sm font-medium text-gray-900">{row[3]}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Value</p>
                          <p className="text-sm font-medium text-gray-900">{row[4]}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          }}
          emptyMessage="No inventory items found"
        />
      </div>

      <div className="mt-4 flex justify-between items-center">
        <div className="text-sm text-gray-500">
          Showing {filteredItems.length} of {items.length} items
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" disabled>
            Previous
          </Button>
          <Button variant="outline" disabled>
            Next
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InventoryList;